import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Share2 } from "lucide-react";
import { toast } from "sonner";
import { mergeContent } from "../lib/api";
import { useSite } from "../lib/SiteContext";
import BrandWordmark from "./BrandWordmark";

export default function Footer({ settings }) {
  const { settings: ctxSettings } = useSite();
  const s = settings || ctxSettings;
  const c = mergeContent(s);
  const email = s?.contact_email || "";

  const share = async () => {
    const url = window.location.href.split("#")[0];
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: `${c.site_title} — Fine Art Portfolio`, url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("Link copied — send it to anyone");
  };

  return (
    <footer id="contact" data-testid="site-footer" className="bg-panel/30 scroll-mt-40">
      <div className="metal-rule h-px w-full" />
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-28">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          {c.footer_eyebrow && (
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-12 rule-flare" />
              <p className="eyebrow !text-ochre/90">{c.footer_eyebrow}</p>
            </div>
          )}
          {c.footer_title && (
            <h2 className="font-display font-light text-4xl md:text-6xl leading-tight max-w-3xl">
              {c.footer_title}
            </h2>
          )}
          {email && (
            <a
              href={`mailto:${email}`}
              data-testid="footer-contact-link"
              className="btn-metal mt-10 inline-flex items-center gap-3 px-8 py-4 font-meta text-[11px] uppercase tracking-[0.3em] text-ink"
            >
              Write to the Studio <ArrowUpRight size={14} />
            </a>
          )}
        </motion.div>

        <div className="mt-16 md:mt-24 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between font-meta text-[9px] md:text-[10px] uppercase tracking-[0.25em] text-mute">
          <span className="flex items-center gap-6 flex-wrap">
            <span className="flex items-center gap-4 flex-wrap">
              <BrandWordmark siteTitle={c.site_title} variant="footer" />
              <span>© {new Date().getFullYear()} — All rights reserved</span>
            </span>
            <Link
              to="/news"
              data-testid="footer-news-link"
              className="hover:text-ochre transition-colors duration-300"
            >
              News &amp; Shows
            </Link>
          </span>
          <button
            onClick={share}
            data-testid="footer-share-button"
            className="flex items-center gap-2 border border-line px-5 py-3 text-bone/80 hover:text-ochre hover:border-ochre/50 transition-colors duration-300"
          >
            <Share2 size={13} /> Share this site
          </button>
        </div>
      </div>
    </footer>
  );
}
